'use client'

import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import { Loader2, Trophy } from 'lucide-react'
import { motion } from 'motion/react'

import { getWins } from '@/app/actions/win'
import { Button } from '@/components/ui/button'
import WinTile from '@/components/WinsComps/WinTile'
import { cn } from '@/utilities/ui'

import type { Topic, Win } from '@/payload-types'

export const WinTracker = ({
  userTopics,
  userState,
}: {
  userTopics: Topic[]
  userState?: string
}) => {
  const [wins, setWins] = useState<Win[] | null>(null)
  const [loading, setLoading] = useState(true)

  const topicIds = useMemo(
    () => userTopics.map((t) => (typeof t === 'number' ? t : t.id)),
    [userTopics],
  )
  const topicIdsKey = topicIds.join(',')

  type Variant = { state?: string; topics?: number[] }
  const paramVariants = useMemo<Variant[]>(() => {
    const ids = topicIdsKey
      ? topicIdsKey
          .split(',')
          .map((id) => parseInt(id, 10))
          .filter((n) => !Number.isNaN(n))
      : []
    const hasTopics = ids.length > 0
    const variants: Variant[] = []
    variants.push({
      ...(userState && { state: userState }),
      ...(hasTopics && { topics: ids }),
    })
    if (userState && hasTopics) {
      variants.push({ topics: ids })
      variants.push({ state: userState })
    }
    if (userState || hasTopics) variants.push({})
    return variants
  }, [topicIdsKey, userState])

  const baseParams = useMemo(() => ({ limit: 10, sort: '-winDate' as const }), [])

  useEffect(() => {
    let cancelled = false
    const id = requestAnimationFrame(() => {
      if (!cancelled) setLoading(true)
    })

    const tryNext = async (index: number) => {
      if (cancelled || index >= paramVariants.length) {
        if (!cancelled) {
          setWins([])
        }
        setLoading(false)
        return
      }

      const variant = paramVariants[index]
      const list = await getWins({
        ...baseParams,
        state: variant.state,
        topics: variant.topics?.length ? variant.topics : undefined,
      })
      if (cancelled) return
      if (list && list.length > 0) {
        setWins(list)
        setLoading(false)
      } else {
        tryNext(index + 1)
      }
    }

    tryNext(0)

    return () => {
      cancelled = true
      cancelAnimationFrame(id)
    }
  }, [paramVariants, baseParams])

  if (!loading && !wins?.length) {
    return null
  }

  return (
    <motion.div
      initial={false}
      animate={{ maxHeight: '60vh' }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={cn(
        'flex grow flex-col overflow-clip rounded-2xl border border-border bg-background p-4 lg:p-6',
      )}
    >
      {loading && (
        <div className="flex min-h-30 flex-col items-center justify-center gap-3">
          <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">Loading wins…</p>
        </div>
      )}
      {!loading && wins && wins.length > 0 && (
        <>
          <div className="mb-6 flex flex-row justify-between">
            <div className="flex flex-row items-center gap-1">
              <h2 className="flex flex-row items-center gap-2 font-body text-xl font-medium text-foreground">
                <Trophy className="size-6" />
                Notable Wins
              </h2>
            </div>
            <Button variant="outline" size="small" asChild>
              <Link href={'/maha-wins?submit-win'}>Submit win</Link>
            </Button>
          </div>
          <div className="flex min-h-0 flex-1 flex-wrap gap-4 overflow-y-auto *:shrink-0">
            {wins.map((win) => (
              <WinTile className="w-full" key={win.id} win={win} />
            ))}
            <div className="mx-auto pt-2 pb-1">
              <Button variant="outline" size="small" className="w-full" asChild>
                <Link href={'/maha-wins'}>Explore more wins</Link>
              </Button>
            </div>
          </div>
        </>
      )}
    </motion.div>
  )
}
